import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ScanLine, Map, Calculator, HeartHandshake, Sparkles, CheckCircle2 } from "lucide-react";
import Logo from "@/components/Logo";
import PoweredBy from "@/components/PoweredBy";
import IntroVideo from "@/components/IntroVideo";
import { useAuth } from "@/context/AuthContext";

const HERO_IMG = "https://images.unsplash.com/photo-1753164726626-e4c38056a03f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const GROW_IMG = "https://images.unsplash.com/photo-1753161618037-e6a8f740fd47?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";

const FEATURES = [
  { icon: Sparkles, title: "A judgment-free diagnostic", desc: "A few honest questions — no math, no shame — to meet you exactly where you are." },
  { icon: Map, title: "Your personalized roadmap", desc: "A week-by-week path so you always know the one thing to do next." },
  { icon: ScanLine, title: "The P&L Decoder", desc: "Type in three numbers. We translate your Profit & Loss into plain English." },
  { icon: Calculator, title: "Profit Growth Calculator", desc: "See how small 5% moves on the 'Big Three' compound into real profit." },
];

export default function Landing() {
  const { user, tenant } = useAuth();
  const cuParam = tenant?.slug && tenant.slug !== "fisbo" ? `?cu=${tenant.slug}` : "";

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <header className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-5 sm:px-6 lg:px-8">
        <Logo />
        <div className="flex shrink-0 items-center gap-2 sm:gap-3">
          {user ? (
            <Link data-testid="landing-dashboard-link" to="/dashboard" className="rounded-full px-5 py-2.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5" style={{ backgroundColor: "var(--tenant-primary)" }}>
              Go to dashboard
            </Link>
          ) : (
            <>
              <Link data-testid="landing-login-link" to={`/login${cuParam}`} className="rounded-full px-4 py-2.5 text-sm font-medium text-neutral-700 hover:bg-neutral-100">Sign in</Link>
              <Link data-testid="landing-register-link" to={`/diagnostic${cuParam}`} className="rounded-full px-5 py-2.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5" style={{ backgroundColor: "var(--tenant-primary)" }}>Get started free</Link>
            </>
          )}
        </div>
      </header>

      {/* Hero */}
      <section className="mx-auto flex max-w-7xl flex-col gap-8 px-4 pb-20 pt-10 sm:px-6 lg:grid lg:grid-cols-2 lg:items-start lg:gap-12 lg:px-8 lg:pt-16">
        {/* Text — top */}
        <div className="fade-up order-1">
          {tenant?.slug !== "fisbo" && (
            <div className="mb-5 inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold tenant-text" style={{ backgroundColor: "var(--tenant-primary-light)" }}>
              <HeartHandshake size={14} /> A member benefit from {tenant?.name}
            </div>
          )}
          <h1 className="font-head text-4xl font-semibold leading-[1.1] tracking-tight text-neutral-900 sm:text-5xl lg:text-[3.4rem]">
            Finally get what your numbers are<span className="tenant-text"> saying.</span>
          </h1>
          <p className="mt-6 max-w-xl text-lg leading-relaxed text-neutral-600">
            FISBO Compass — the financial clarity tool for owners who never learned the language of business.
          </p>
          {/* Main CTA — desktop only (mobile uses the button under the video) */}
          <div className="mt-8 hidden flex-col items-start gap-3 lg:flex">
            <Link data-testid="hero-cta" to={user ? "/dashboard" : `/diagnostic${cuParam}`} className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-base font-semibold text-white shadow-sm transition-all hover:-translate-y-0.5" style={{ backgroundColor: "var(--tenant-primary)" }}>
              Take the free diagnostic <ArrowRight size={18} />
            </Link>
            <span className="text-sm text-neutral-500">No credit card · Judgment-free</span>
          </div>
        </div>

        {/* Media — intro video + button directly beneath it */}
        <div className="fade-up order-2 lg:row-span-2 lg:self-center" style={{ animationDelay: "0.15s" }}>
          <IntroVideo
            testid="hero-video"
            title="Start here: see where your business really stands"
            subtitle="Watch Penny & Ollie (1 min), then take the free 7-question diagnostic."
            embedUrl="https://dubb.com/v/37SpXJ/embed?width=auto&height=auto&autoplay=0&no_cta=1&no_controls=0&muted=0"
          />
          <div className="mt-5 flex flex-col items-start gap-3">
            <Link data-testid="hero-cta-video" to={user ? "/dashboard" : `/diagnostic${cuParam}`} className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-base font-semibold text-white shadow-sm transition-all hover:-translate-y-0.5" style={{ backgroundColor: "var(--tenant-primary)" }}>
              Take the free diagnostic <ArrowRight size={18} />
            </Link>
            <span className="text-sm text-neutral-500 lg:hidden">No credit card · Judgment-free</span>
          </div>
        </div>

        {/* Text — bottom (checkmark points) */}
        <div className="fade-up order-3">
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-neutral-600">
            {["Built for owners, not accountants", "Plain English, always", "One step at a time"].map((t) => (
              <span key={t} className="inline-flex items-center gap-2"><CheckCircle2 size={16} className="tenant-text" /> {t}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Insight band */}
      <section id="how-it-works" className="border-y border-[#E5E2DC] bg-white">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:px-8">
          <div className="overflow-hidden rounded-[2rem] border border-[#E5E2DC]">
            <img src={GROW_IMG} alt="A small business owner reviewing their numbers on a laptop in their shop" className="h-80 w-full object-cover" />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-neutral-500">The core idea</p>
            <h2 className="mt-3 font-head text-2xl font-semibold tracking-tight text-neutral-900 sm:text-3xl">Most financial education is built by accountants, for accountants.</h2>
            <p className="mt-4 text-lg leading-relaxed text-neutral-600">It uses jargon, assumes you already know the basics, and quietly makes you feel stupid. The shame becomes the real block — not the math. <strong className="font-semibold text-neutral-900">FISBO Compass</strong> is the opposite: it names the gap without blame and walks you through it, one small win at a time.</p>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="mb-12 max-w-2xl">
          <h2 className="font-head text-2xl font-semibold tracking-tight text-neutral-900 sm:text-3xl">Everything you need, in the right sequence</h2>
          <p className="mt-3 text-lg text-neutral-600">Not a class. Not a spreadsheet. A guided experience that turns the FISBO book into a living action plan.</p>
        </div>
        <div className="grid gap-6 sm:grid-cols-2">
          {FEATURES.map((f, i) => (
            <div key={f.title} className="fade-up rounded-2xl border border-[#E5E2DC] bg-white p-8 transition-all duration-300 hover:-translate-y-0.5" style={{ animationDelay: `${i * 0.08}s` }} data-testid={`feature-${i}`}>
              <span className="mb-5 grid h-12 w-12 place-items-center rounded-xl" style={{ backgroundColor: "var(--tenant-primary-light)" }}>
                <f.icon className="tenant-text" strokeWidth={1.5} />
              </span>
              <h3 className="font-head text-xl font-medium text-neutral-900">{f.title}</h3>
              <p className="mt-2 leading-relaxed text-neutral-600">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
        <div className="rounded-[2rem] px-8 py-16 text-center" style={{ backgroundColor: "var(--tenant-primary)" }}>
          <h2 className="mx-auto max-w-2xl font-head text-3xl font-semibold tracking-tight text-white sm:text-4xl">Let's find the one thing to do this week.</h2>
          <p className="mx-auto mt-4 max-w-xl text-lg text-white/85">Start with a short honest diagnostic. We'll build your roadmap together.</p>
          <Link data-testid="cta-bottom" to={user ? "/dashboard" : `/diagnostic${cuParam}`} className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-base font-semibold tenant-text transition-all hover:-translate-y-0.5">
            Get started free <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <footer className="border-t border-[#E5E2DC] bg-white">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-4 py-8 sm:flex-row sm:px-6 lg:px-8">
          <div className="flex flex-col items-center gap-1.5 sm:items-start">
            <Logo />
            <PoweredBy />
          </div>
          <p className="text-sm text-neutral-500">© 2026 FISBO Compass · Financial Intelligence for Small Business Owners</p>
        </div>
      </footer>
    </div>
  );
}
